/**
 * The graph (Fase 7a): pages as dots, [[links]] as lines, laid out by a force
 * simulation on a canvas. Scroll to zoom, drag the background to pan, drag a
 * dot to move it, click a dot to open the page; hovering highlights a page and
 * its neighbours. Filters: page tree, orphans, tag, area, hidden-from-AI; the
 * local graph shows the pages around one page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  forceCenter,
  forceX,
  forceY,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import { aiScope } from '@nexus/ai';
import { buildGraph, type GraphNode } from '@nexus/core';
import { useStoreQuery } from '../db/context';
import { useUi } from '../state/ui';

type Node = GraphNode & SimulationNodeDatum & { r: number };
type Edge = SimulationLinkDatum<Node> & { kind: 'link' | 'parent' };

/** Area colours that read on light and dark backgrounds. */
const PALETTE = [
  '#0099ff',
  '#e8590c',
  '#2f9e44',
  '#ae3ec9',
  '#f08c00',
  '#1098ad',
  '#d6336c',
  '#5c7cfa',
];
const field = 'rounded-lg border border-border bg-bg px-2 py-1 text-sm';

function cssVar(name: string, fallback: string) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function GraphView({ center: initialCenter }: { center?: string | null }) {
  const { t } = useTranslation();
  const open = useUi((s) => s.open);
  const data = useStoreQuery(
    async (s) => ({ pages: await s.listPagesWithProps(), ...(await s.graphData()) }),
    [],
    ['pages', 'index', 'props'],
  );
  const [hierarchy, setHierarchy] = useState(false);
  const [orphans, setOrphans] = useState(false);
  const [hideAiHidden, setHideAiHidden] = useState(false);
  const [tag, setTag] = useState('');
  const [area, setArea] = useState('');
  const [center, setCenter] = useState<string | null>(initialCenter ?? null);
  const [depth, setDepth] = useState(1);
  const [query, setQuery] = useState('');

  const graph = useMemo(() => {
    if (!data) return null;
    const hidden = aiScope(data.pages).excluded;
    return buildGraph(
      data.pages.map((p) => ({
        id: p.id,
        title: p.title || t('page.untitled'),
        parentId: p.parentId,
        aiHidden: hidden.has(p.id),
      })),
      data.links,
      data.tags,
      { hierarchy, orphans, hideAiHidden, tag: tag || null, area: area || null, center, depth },
    );
  }, [data, hierarchy, orphans, hideAiHidden, tag, area, center, depth, t]);

  const roots = useMemo(() => (data?.pages ?? []).filter((p) => !p.parentId), [data]);
  const allTags = useMemo(() => [...new Set((data?.tags ?? []).map((x) => x.tag))].sort(), [data]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q && graph
      ? graph.nodes.filter((n) => n.title.toLowerCase().includes(q)).slice(0, 8)
      : [];
  }, [graph, query]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="graph">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-sm">
        <h1 className="mr-2 text-base font-semibold">{t('graph.title')}</h1>
        <div className="relative">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('graph.search')}
            aria-label={t('graph.search')}
            className={`${field} w-48`}
          />
          {matches.length > 0 && (
            <ul className="absolute top-full left-0 z-10 mt-1 w-64 rounded-lg border border-border bg-bg p-1 shadow-lg">
              {matches.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setCenter(n.id);
                      setQuery('');
                    }}
                    className="w-full truncate rounded px-2 py-1 text-left hover:bg-bg-hover"
                  >
                    {n.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <select
          value={area}
          onChange={(e) => setArea(e.target.value)}
          aria-label={t('graph.area')}
          className={field}
        >
          <option value="">{t('graph.allAreas')}</option>
          {roots.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title || t('page.untitled')}
            </option>
          ))}
        </select>
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          aria-label={t('graph.tag')}
          className={field}
        >
          <option value="">{t('graph.allTags')}</option>
          {allTags.map((x) => (
            <option key={x} value={x}>
              #{x}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={hierarchy}
            onChange={(e) => setHierarchy(e.target.checked)}
          />
          {t('graph.hierarchy')}
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={orphans} onChange={(e) => setOrphans(e.target.checked)} />
          {t('graph.orphans')}
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={hideAiHidden}
            onChange={(e) => setHideAiHidden(e.target.checked)}
          />
          {t('graph.hideAiHidden')}
        </label>
        {center && (
          <span className="flex items-center gap-1 rounded-full bg-accent/10 px-2 py-1">
            {t('graph.around', { title: graph?.nodes.find((n) => n.id === center)?.title ?? '' })}
            <select
              value={depth}
              onChange={(e) => setDepth(Number(e.target.value))}
              aria-label={t('graph.depth')}
              className="rounded bg-transparent"
            >
              {[1, 2, 3].map((d) => (
                <option key={d} value={d}>
                  {t('graph.steps', { count: d })}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => setCenter(null)} className="underline">
              {t('graph.whole')}
            </button>
          </span>
        )}
      </div>
      {graph && (
        <Canvas
          nodes={graph.nodes}
          edges={graph.edges}
          focus={center}
          label={t('graph.label', { pages: graph.nodes.length, links: graph.edges.length })}
          onOpen={(id) => open({ kind: 'page', pageId: id })}
        />
      )}
      {graph && graph.nodes.length === 0 && (
        <p className="p-6 text-center text-sm text-fg-muted">{t('graph.empty')}</p>
      )}
    </div>
  );
}

function Canvas({
  nodes: input,
  edges: inputEdges,
  focus,
  label,
  onOpen,
}: {
  nodes: GraphNode[];
  edges: { source: string; target: string; kind: 'link' | 'parent' }[];
  focus: string | null;
  label: string;
  onOpen: (id: string) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  // The latest callback, without restarting the simulation on every render.
  const openRef = useRef(onOpen);
  useEffect(() => {
    openRef.current = onOpen;
  });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const colors = new Map<string, string>();
    const colorOf = (areaId: string) => {
      if (!colors.has(areaId)) colors.set(areaId, PALETTE[colors.size % PALETTE.length]!);
      return colors.get(areaId)!;
    };
    const nodes: Node[] = input.map((n) => ({ ...n, r: 3 + Math.min(9, Math.sqrt(n.degree) * 2) }));
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const edges: Edge[] = inputEdges.map((e) => ({ ...e }));
    const neighbours = new Map<string, Set<string>>();
    for (const e of inputEdges) {
      neighbours.set(e.source, (neighbours.get(e.source) ?? new Set()).add(e.target));
      neighbours.set(e.target, (neighbours.get(e.target) ?? new Set()).add(e.source));
    }

    let view = { x: 0, y: 0, k: 1 };
    let touched = false;
    let hover: Node | null = null;
    let size = { w: 0, h: 0 };
    const fg = cssVar('--fg', '#222');
    const muted = cssVar('--fg-muted', '#888');

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.save();
      ctx.translate(size.w / 2 + view.x, size.h / 2 + view.y);
      ctx.scale(view.k, view.k);
      const lit = hover ? new Set([hover.id, ...(neighbours.get(hover.id) ?? [])]) : null;
      for (const e of edges) {
        const s = e.source as Node;
        const t = e.target as Node;
        const on = lit && lit.has(s.id) && lit.has(t.id);
        ctx.globalAlpha = lit ? (on ? 0.9 : 0.08) : e.kind === 'parent' ? 0.25 : 0.4;
        ctx.strokeStyle = on ? colorOf(hover!.area) : muted;
        ctx.lineWidth = (on ? 1.5 : 0.8) / view.k;
        if (e.kind === 'parent') ctx.setLineDash([3 / view.k, 3 / view.k]);
        ctx.beginPath();
        ctx.moveTo(s.x!, s.y!);
        ctx.lineTo(t.x!, t.y!);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      for (const n of nodes) {
        ctx.globalAlpha = lit && !lit.has(n.id) ? 0.2 : 1;
        ctx.fillStyle = colorOf(n.area);
        ctx.beginPath();
        ctx.arc(n.x!, n.y!, n.r, 0, Math.PI * 2);
        ctx.fill();
        if (n.id === focus) {
          ctx.strokeStyle = fg;
          ctx.lineWidth = 2 / view.k;
          ctx.stroke();
        }
      }
      // Labels: the hovered page and its neighbours, the centre, and everything when zoomed in.
      ctx.font = `${12 / view.k}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      for (const n of nodes) {
        const show =
          (lit ? lit.has(n.id) : nodes.length <= 80 || view.k > 1.4 || n.degree >= 6) ||
          n.id === focus;
        if (!show) continue;
        ctx.globalAlpha = 1;
        ctx.fillStyle = fg;
        ctx.fillText(
          n.title.length > 40 ? `${n.title.slice(0, 39)}…` : n.title,
          n.x!,
          n.y! + n.r + 12 / view.k,
        );
      }
      ctx.restore();
    };

    /** Zoom and pan so that every dot fits in the window (at most 2.5×). */
    const fit = () => {
      if (!nodes.length || !size.w) return;
      const pad = 48;
      const xs = nodes.map((n) => n.x!);
      const ys = nodes.map((n) => n.y!);
      let [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      // A local graph stays centred on its page (fixed at 0,0).
      if (focus && byId.has(focus)) {
        const rx = Math.max(-x0, x1);
        const ry = Math.max(-y0, y1);
        [x0, x1, y0, y1] = [-rx, rx, -ry, ry];
      }
      const k = Math.min(
        2.5,
        (size.w - 2 * pad) / Math.max(1, x1 - x0),
        (size.h - 2 * pad) / Math.max(1, y1 - y0),
      );
      view = { k, x: -((x0 + x1) / 2) * k, y: -((y0 + y1) / 2) * k };
    };

    const sim = forceSimulation(nodes)
      .force(
        'link',
        forceLink<Node, Edge>(edges)
          .id((d) => d.id)
          .distance((e) => (e.kind === 'parent' ? 30 : 50)),
      )
      .force('charge', forceManyBody<Node>().strength(-70))
      .force('center', forceCenter(0, 0))
      // A gentle pull to the middle keeps unlinked pages close.
      .force('x', forceX<Node>(0).strength(0.06))
      .force('y', forceY<Node>(0).strength(0.06))
      .force(
        'collide',
        forceCollide<Node>((n) => n.r + 2),
      )
      .alphaDecay(0.03)
      .on('tick', () => {
        // Fit the drawing to the window once the layout settles, unless the user moved it.
        if (!touched && sim.alpha() < 0.25) fit();
        draw();
      });
    if (focus && byId.get(focus)) {
      const f = byId.get(focus)!;
      f.fx = 0;
      f.fy = 0;
    }

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      size = { w: r.width, h: r.height };
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      draw();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const toWorld = (e: { clientX: number; clientY: number }) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - r.left - size.w / 2 - view.x) / view.k,
        y: (e.clientY - r.top - size.h / 2 - view.y) / view.k,
      };
    };
    const hit = (e: { clientX: number; clientY: number }) => {
      const p = toWorld(e);
      let best: Node | null = null;
      let bestD = Infinity;
      for (const n of nodes) {
        const d = Math.hypot(n.x! - p.x, n.y! - p.y);
        if (d < n.r + 4 / view.k && d < bestD) {
          best = n;
          bestD = d;
        }
      }
      return best;
    };

    let drag: {
      node: Node | null;
      startX: number;
      startY: number;
      moved: boolean;
      vx: number;
      vy: number;
    } | null = null;
    const onDown = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      drag = {
        node: hit(e),
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        vx: view.x,
        vy: view.y,
      };
    };
    const onMove = (e: PointerEvent) => {
      if (!drag) {
        const h = hit(e);
        if (h !== hover) {
          hover = h;
          canvas.style.cursor = h ? 'pointer' : 'grab';
          draw();
        }
        return;
      }
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (Math.hypot(dx, dy) > 3) drag.moved = true;
      if (drag.node) {
        const p = toWorld(e);
        drag.node.fx = p.x;
        drag.node.fy = p.y;
        sim.alphaTarget(0.2).restart();
      } else {
        touched = true;
        view = { ...view, x: drag.vx + dx, y: drag.vy + dy };
        draw();
      }
    };
    const onUp = () => {
      if (drag?.node) {
        if (!drag.moved) openRef.current(drag.node.id);
        if (drag.node.id !== focus) {
          drag.node.fx = null;
          drag.node.fy = null;
        }
        sim.alphaTarget(0);
      }
      drag = null;
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const mx = e.clientX - r.left - size.w / 2;
      const my = e.clientY - r.top - size.h / 2;
      const k = Math.min(6, Math.max(0.15, view.k * Math.exp(-e.deltaY * 0.0015)));
      // Zoom around the pointer.
      touched = true;
      view = { k, x: mx - ((mx - view.x) * k) / view.k, y: my - ((my - view.y) * k) / view.k };
      draw();
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      sim.stop();
      observer.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [input, inputEdges, focus]);

  return (
    <canvas
      ref={ref}
      role="img"
      aria-label={label}
      className="min-h-0 w-full flex-1 cursor-grab touch-none"
    />
  );
}
